/** Staff who can use the in-house board and be assigned a task. */
export const STAFF_ROLES = ['agent', 'owner', 'admin', 'administrator'];

export const isStaffRole = (role) => STAFF_ROLES.includes(String(role || '').toLowerCase());

export const staffLabel = (user) => {
  if (!user) return 'Staff';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.displayName || user.email || 'Staff';
};

export const BOARD_FILE_MAX = 10 * 1024 * 1024;
export const BOARD_FILE_LIMIT = 4;

const BOARD_FILE_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
]);

/** Images, PDF, Word, Excel, or plain text, up to 10MB. */
export const boardFileOk = (file) => {
  if (!file || !file.size || file.size > BOARD_FILE_MAX) return false;
  const type = String(file.type || '');
  return type.startsWith('image/') || BOARD_FILE_TYPES.has(type);
};

/** Download URLs we are willing to render. Blocks javascript: and odd hosts. */
export const isBoardFileUrl = (url) => (
  /^https:\/\/(firebasestorage\.googleapis\.com|storage\.googleapis\.com)\//.test(String(url || ''))
);

const escapeReg = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** The @query being typed at the caret, or null. */
export const mentionQuery = (text, caret) => {
  const upto = String(text || '').slice(0, Number(caret) || 0);
  const at = upto.lastIndexOf('@');
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(upto[at - 1])) return null;
  const query = upto.slice(at + 1);
  if (/[\s@]/.test(query)) return null;
  return { at, query };
};

/** Options whose label contains the query. excludeId skips the current user. */
export const mentionMatches = (options, query, excludeId) => {
  const q = String(query || '').trim().toLowerCase();
  return (options || [])
    .filter((option) => option?.id && option.id !== excludeId && String(option.label || '').toLowerCase().includes(q))
    .slice(0, 8);
};

/** Ids named as @Label. Longer names win, so @Ann Lee is not also @Ann. */
export const mentionedIds = (text, options, excludeId) => {
  let masked = String(text || '');
  const ids = [];
  (options || [])
    .filter((option) => option?.id && option.id !== excludeId && option.label)
    .sort((a, b) => b.label.length - a.label.length)
    .forEach((option) => {
      const re = new RegExp(`(^|\\s)@${escapeReg(option.label)}(?=$|[\\s.,!?;:])`, 'i');
      if (!re.test(masked)) return;
      ids.push(option.id);
      masked = masked.replace(re, (match) => ' '.repeat(match.length));
    });
  return ids;
};

/** Split message text so @names can be emphasised. */
export const mentionParts = (text, labels) => {
  const source = String(text || '');
  const names = [...(labels || [])].filter(Boolean).sort((a, b) => b.length - a.length);
  if (!source || !names.length) return [{ text: source, mention: false }];
  const re = new RegExp(`(^|\\s)(@(?:${names.map(escapeReg).join('|')}))(?=$|[\\s.,!?;:])`, 'gi');
  const parts = [];
  let last = 0;
  source.replace(re, (match, lead, name, index) => {
    const start = index + lead.length;
    if (start > last) parts.push({ text: source.slice(last, start), mention: false });
    parts.push({ text: name, mention: true });
    last = start + name.length;
    return match;
  });
  if (last < source.length) parts.push({ text: source.slice(last), mention: false });
  return parts.length ? parts : [{ text: source, mention: false }];
};

export const BOARD_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

/** Toggle one person's emoji on a reactions map. */
export const toggleReactionMap = (reactions, emoji, userId) => {
  if (!BOARD_EMOJIS.includes(emoji) || !userId) return { ...(reactions || {}) };
  const next = { ...(reactions || {}) };
  const list = [...(next[emoji] || [])].filter((id) => id !== userId);
  if (!(next[emoji] || []).includes(userId)) list.push(userId);
  if (list.length) next[emoji] = list;
  else delete next[emoji];
  return next;
};

/** Emoji rows with counts, for rendering under a message. */
export const reactionList = (reactions) => (
  Object.entries(reactions || {})
    .filter(([emoji, ids]) => BOARD_EMOJIS.includes(emoji) && Array.isArray(ids) && ids.length)
    .map(([emoji, ids]) => ({ emoji, count: ids.length, ids }))
);

/** Collapse whitespace and cap length. Empty string means reject. */
export const cleanBoardText = (value, max) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  return text.slice(0, max);
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TONE_RANK = { overdue: 0, today: 1, soon: 2, later: 3, '': 4 };

/** YYYY-MM-DD, or '' when the value is not a calendar day. */
export const cleanDue = (value) => (/^\d{4}-\d{2}-\d{2}$/.test(String(value || '')) ? String(value) : '');

export const todayISO = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const shiftISO = (iso, days) => {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/** overdue, today, soon (within 2 days), later, or '' when there is no due date or the task is done. */
export const dueTone = (due, done, today) => {
  const day = cleanDue(due);
  const now = cleanDue(today);
  if (!day || done || !now) return '';
  if (day < now) return 'overdue';
  if (day === now) return 'today';
  if (day <= shiftISO(now, 2)) return 'soon';
  return 'later';
};

export const dueLabel = (due) => {
  const day = cleanDue(due);
  if (!day) return '';
  const [, month, date] = day.split('-').map(Number);
  return `${date} ${MONTHS[month - 1]}`;
};

/** Pill for a due date, or null when the task has none. */
export const duePill = (due, done, today) => {
  const tone = dueTone(due, done, today);
  const label = dueLabel(due);
  if (tone === 'overdue') return { tone: 'rust', label: `Overdue ${label}` };
  if (tone === 'today') return { tone: 'amber', label: 'Due today' };
  if (tone === 'soon') return { tone: 'amber', label: `Due ${label}` };
  if (tone === 'later') return { tone: 'muted', label: `Due ${label}` };
  return null;
};

/** Open tasks are urgent first. Done keeps the list order. Mine is open tasks assigned to userId. */
export const visibleBoardTasks = (tasks, filter, userId, today) => {
  const list = (tasks || []).filter((task) => {
    if (filter === 'done') return !!task.done;
    if (task.done) return false;
    return filter !== 'mine' || task.assigneeId === userId;
  });
  if (filter === 'done') return list;
  return [...list].sort((a, b) => {
    const rank = (TONE_RANK[dueTone(a.due, false, today)] ?? 4) - (TONE_RANK[dueTone(b.due, false, today)] ?? 4);
    if (rank) return rank;
    const ad = cleanDue(a.due) || '9999-99-99';
    const bd = cleanDue(b.due) || '9999-99-99';
    if (ad !== bd) return ad < bd ? -1 : 1;
    return 0;
  });
};
